import _ from 'lodash';
import moment from 'moment';
import { type ForwardedRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  fetchHolterEvents,
  applyGroupEventChange,
  isAppliedFilterEvent,
  formatEventStrip,
  updateEventChangeAndNew,
} from '@/entities/event';
import {
  KEY_CANCEL,
  KEY_RECOIL,
  STRIP_AFIB_THUMBNAIL_INFO,
  typesPersistEventV2,
} from '@/shared/config';
import { useUpdateDeepCompareEffect, useUpdateEffect } from '@/shared/lib/customHooks';

import { formatHrData } from '@/entities/heart-rate';
import { logError } from '@/shared/lib/apiHelpers';
import {
  selectActiveTab,
  selectCombinedEvents,
  selectDailyCount,
  selectEventChanges,
  selectEventNews,
  selectGroupEventChanges,
  selectIsEmptyData,
  selectEventFilter,
  selectIsLoadingHrChart,
  selectReloadEcgViewer,
  selectReloadHrChart,
  selectSelectedDateValue,
  selectUpdatedSelectedStrip,
  useReportStore,
} from '@/entities/report';
import type { AfibHrChartHandle, AfibHrChartProps } from './afibHrChart';

/**
 * Drives the AFib HR chart for the beats and HR tab, loading the selected day's minutely heart rate and overlaying rhythm-event zones. Subscribes to report-store date, filter and event-change state, refetches and reformats the chart on any of them, prefetches the neighbouring days two seconds later, repages so the updated strip stays visible, and exposes arrow navigation through the forwarded ref.
 *
 * @param props - the study, profile, timezone offset and ECG data map the chart draws from.
 * @param ref - forwarded handle through which the parent triggers arrow navigation.
 * @returns An object carrying keyRecoil, dataChart, updatedSelectedStrip and chartHrRef.
 */
const useAfibHrChart = (
  { studyId, profileId, timezoneOffset, ecgDataMap }: AfibHrChartProps,
  ref: ForwardedRef<AfibHrChartHandle>,
) => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;

  /**
   * Reads the rhythm-event tab currently selected in the beats and HR tab. Pulls the active tab off the report store snapshot rather than subscribing, so callbacks and effects see the latest value.
   *
   * @returns The active rhythm event type for this tab.
   */
  const getActiveButton = () => selectActiveTab(keyRecoil)(useReportStore.getState());
  const selectedDateValue = useReportStore(selectSelectedDateValue(keyRecoil));
  const isLoadingHrChart = useReportStore(selectIsLoadingHrChart(keyRecoil));
  const setIsLoadingHrChartAction = useReportStore((s) => s.actions.setIsLoadingHrChart);
  /**
   * Toggles the HR chart's loading indicator. Forwards the value to the store action already bound to this tab's key.
   *
   * @param v - the new value, or an updater receiving the previous one.
   * @returns The result of the bound store action.
   */
  const setIsLoadingHrChart = (v: Parameters<typeof setIsLoadingHrChartAction>[1]) =>
    setIsLoadingHrChartAction(keyRecoil, v);
  const eventChanges = useReportStore(selectEventChanges(keyRecoil));
  const eventNews = useReportStore(selectEventNews(keyRecoil));
  const groupEventChanges = useReportStore(selectGroupEventChanges(keyRecoil));
  const dateList = useReportStore((s) => s.dateList);
  const eventFilter = useReportStore(selectEventFilter(keyRecoil));
  const reloadHrChart = useReportStore(selectReloadHrChart(keyRecoil));
  // updatedSelectedStripState (Recoil/index.ts, Task 7) is typed IEvent | IBeat | IEcgBookmark | null; this
  // file reads event-only fields (isNew, idEvent) that IBeat/IEcgBookmark lack - pre-existing gap.
  const updatedSelectedStrip = useReportStore(selectUpdatedSelectedStrip(keyRecoil)) as any;
  const setCombinedEventsAction = useReportStore((s) => s.actions.setCombinedEvents);
  /**
   * Writes the merged holter events for the beats and HR tab. Forwards the value to the store action already bound to this tab's key.
   *
   * @param v - the new value, or an updater receiving the previous one.
   * @returns The result of the bound store action.
   */
  const setCombinedEvents = (v: Parameters<typeof setCombinedEventsAction>[1]) =>
    setCombinedEventsAction(keyRecoil, v);
  /**
   * Reads the merged holter events currently backing the HR chart. Selects the combined-events slice from the report store snapshot instead of subscribing, keeping callbacks free of stale closures.
   *
   * @returns The combined holter events for this tab.
   */
  const getCombinedEvents = () => selectCombinedEvents(keyRecoil)(useReportStore.getState());
  /**
   * Reads the per-day event counts driving the date dropdown and the page arithmetic. Selects the daily-count slice from the report store snapshot rather than subscribing.
   *
   * @returns The per-day event count entries for this tab.
   */
  const getDailyCount = () => selectDailyCount(keyRecoil)(useReportStore.getState());
  /**
   * Reads whether the selected report day has any chartable HR data. Selects the empty-data flag from the report store snapshot rather than subscribing.
   *
   * @returns True when the selected day holds no data.
   */
  const getIsEmptyData = () => selectIsEmptyData(keyRecoil)(useReportStore.getState());
  const setIsEmptyDataAction = useReportStore((s) => s.actions.setIsEmptyData);
  /**
   * Flags whether the selected report day has any chartable data. Forwards the value to the store action already bound to this tab's key.
   *
   * @param v - the new value, or an updater receiving the previous one.
   * @returns The result of the bound store action.
   */
  const setIsEmptyData = (v: Parameters<typeof setIsEmptyDataAction>[1]) =>
    setIsEmptyDataAction(keyRecoil, v);
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Moves the strip pagination to another page. Forwards the value to the store action already bound to this tab's key.
   *
   * @param v - the new value, or an updater receiving the previous one.
   * @returns The result of the bound store action.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  const reloadEcgViewer = useReportStore(selectReloadEcgViewer(keyRecoil));
  const isRenderViewerEcg = useReportStore((s) => s.isRenderViewerEcg);

  const [dataChart, setDataChart] = useState<any>();

  const lastFilterRef = useRef(eventFilter);
  const fetchInBackgroundTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chartHrRef = useRef<any>(null);

  let isActiveQuery: boolean;

  /**
   * Warms the holter-event cache for a neighbouring report day so switching dates renders without a fetch delay. Builds a persist-event filter from the study and profile ids plus the persisted rhythm types and the given window, fires the holter-event fetch under the holter-AI cancel key, and logs any failure without rethrowing.
   *
   * @param startSearchDate - ISO timestamp beginning the day window to prefetch.
   * @param stopSearchDate - ISO timestamp ending that window.
   * @returns A promise resolving once the background fetch has been dispatched.
   */
  const fetchHolterEventsBackgroundV2 = async (startSearchDate: any, stopSearchDate: any) => {
    try {
      const holterEventsPersistFilter = {
        studyId,
        profileId,
        types: typesPersistEventV2,
        startSearchDate,
        stopSearchDate,
      };
      fetchHolterEvents(holterEventsPersistFilter, 0, true, KEY_CANCEL.API_HOLTER_AI);
    } catch (error) {
      logError('Failed to fetch holter events in background: ', error);
    }
  };

  /**
   * Narrows a list of holter events to those the active event filter allows for one rhythm type. Returns the list untouched when no filter applies, otherwise drops events of that type which carry no strip images under the capture filter or which are already reviewed and unchanged under the hide-reviewed filter.
   *
   * @param events - the holter events to filter.
   * @param type - the rhythm event type the filter applies to.
   * @returns The events that survive the capture and hide-reviewed filters.
   */
  const filterEvents = (events: any[], type: any) => {
    if (!isAppliedFilterEvent(eventFilter, type)) {
      return events;
    }
    let keepEvent;
    return _.filter(events, (event: any) => {
      keepEvent = true;
      if (event.type === type) {
        if (eventFilter.isCapture && _.isEmpty(event.stripImgs)) {
          keepEvent = false;
        }
        if (eventFilter.isHideReviewed && event.isReviewed && !event.isChanged) {
          keepEvent = false;
        }
        return keepEvent;
      }
      return keepEvent;
    });
  };

  /**
   * Loads the persisted rhythm events for the selected report day and rebuilds the HR chart from them. Fetches one day of holter events, sorts them by start time, applies pending event changes, new events and group changes, filters by the tab's event filter, stores the result as the combined events and hands the persist-typed subset to the HR formatter; a stale-query flag discards results from a superseded day.
   *
   * @param needLoading - when true, raises the chart loading flag before fetching.
   * @returns A promise resolving once the chart data has been rebuilt or the fetch has failed.
   */
  const fetchHolterEventsByDateV2 = async (needLoading = true) => {
    if (needLoading) {
      setIsLoadingHrChart(true);
    }
    let downloadedPersistEvents: any[] = [];
    try {
      const start = performance.now();
      const type = getActiveButton();
      const startSearchDate = selectedDateValue;
      const stopSearchDate = moment(selectedDateValue).add(1, 'days')?.toISOString();
      const holterEventsPersistFilter = {
        studyId,
        profileId,
        types: typesPersistEventV2,
        startSearchDate,
        stopSearchDate,
      };
      const promise = [fetchHolterEvents(holterEventsPersistFilter, 0, true, null, false)];
      const [resultPersist] = await Promise.all(promise);
      if (performance.now() - start < 100) {
        setIsLoadingHrChart(false);
      }
      if (resultPersist?.events) {
        downloadedPersistEvents = [...resultPersist.events];
      }
      if (isActiveQuery) {
        const mergeEvents = downloadedPersistEvents.sort(
          (a: any, b: any) => Date.parse(a.start) - Date.parse(b.start),
        );
        const dataFormat = formatEventStrip(mergeEvents);
        const holterEventsByDateUpdate = updateEventChangeAndNew(
          dataFormat,
          eventChanges,
          eventNews,
          startSearchDate,
          stopSearchDate,
          typesPersistEventV2,
          true,
        );
        const filterHolterEventsByDateUpdate = filterEvents(holterEventsByDateUpdate, type);
        applyGroupEventChange(filterHolterEventsByDateUpdate, groupEventChanges);
        setCombinedEvents(filterHolterEventsByDateUpdate);
        const holterEventsByDate = filterHolterEventsByDateUpdate.filter(
          (x: any) =>
            typesPersistEventV2.includes(x.type) || typesPersistEventV2.includes(x.oldType),
        );
        formatHrData({
          holterEventsByDate,
          needLoading,
          ecgDataMap,
          timezoneOffset,
          selectedDateValue,
          setDataChart,
          setIsEmptyData,
          setIsLoadingHrChart,
        });
      }
    } catch (error) {
      logError('Failed to fetch holter events: ', error);
    }
  };

  useEffect(() => {
    // *: Change to other date
    isActiveQuery = true;
    if (selectedDateValue && isRenderViewerEcg) {
      chartHrRef.current.resetCurrentSelect();
      fetchHolterEventsByDateV2();
    }
    return () => {
      isActiveQuery = false;
    };
  }, [selectedDateValue, eventFilter, reloadHrChart, isRenderViewerEcg]);

  useEffect(() => {
    if (dateList.length) {
      const currentSelectedDateIndex = _.findIndex(
        dateList,
        (x) => x.toISOString() === selectedDateValue,
      );
      if (currentSelectedDateIndex > -1) {
        if (currentSelectedDateIndex === 0) {
          const firstStartSearchDate = moment(dateList[currentSelectedDateIndex + 1]).toISOString();
          const firstStopSearchDate = moment(firstStartSearchDate).add(1, 'days').toISOString();
          fetchInBackgroundTimeout.current = setTimeout(() => {
            fetchHolterEventsBackgroundV2(firstStartSearchDate, firstStopSearchDate);
          }, 2000);
        } else if (currentSelectedDateIndex === dateList.length - 1) {
          const firstStartSearchDate = moment(dateList[currentSelectedDateIndex - 1]).toISOString();
          const firstStopSearchDate = moment(firstStartSearchDate).add(1, 'days').toISOString();
          fetchInBackgroundTimeout.current = setTimeout(() => {
            fetchHolterEventsBackgroundV2(firstStartSearchDate, firstStopSearchDate);
          }, 2000);
        } else {
          const firstStartSearchDate = moment(dateList[currentSelectedDateIndex + 1]).toISOString();
          const firstStopSearchDate = moment(firstStartSearchDate).add(1, 'days').toISOString();
          const secondStartSearchDate = moment(
            dateList[currentSelectedDateIndex - 1],
          ).toISOString();
          const secondStopSearchDate = moment(secondStartSearchDate).add(1, 'days').toISOString();
          fetchInBackgroundTimeout.current = setTimeout(() => {
            fetchHolterEventsBackgroundV2(firstStartSearchDate, firstStopSearchDate);
            fetchInBackgroundTimeout.current = setTimeout(() => {
              fetchHolterEventsBackgroundV2(secondStartSearchDate, secondStopSearchDate);
            }, 2000);
          }, 2000);
        }
      }
    }
    return () => {
      clearTimeout(fetchInBackgroundTimeout.current);
      fetchInBackgroundTimeout.current = null;
    };
  }, [dateList, selectedDateValue]);

  // *: Fetch and re-draw events
  useUpdateDeepCompareEffect(
    () => {
      isActiveQuery = true;
      if (selectedDateValue && isRenderViewerEcg) {
        fetchHolterEventsByDateV2(false);
      }
    },
    [eventChanges, eventNews, groupEventChanges, reloadEcgViewer, isRenderViewerEcg],
    () => {
      isActiveQuery = false;
    },
  );

  useUpdateEffect(() => {
    if (!isLoadingHrChart) {
      const activeButton = getActiveButton();
      const isAppliedFilter = isAppliedFilterEvent(eventFilter, activeButton);
      const lastFilter = _.cloneDeep(lastFilterRef.current);
      const isResetSort =
        lastFilter.sortBy && lastFilter.sortOrder && !eventFilter.sortOrder && !eventFilter.sortBy;
      lastFilterRef.current = eventFilter;
      if (
        !isAppliedFilter &&
        updatedSelectedStrip &&
        !updatedSelectedStrip.isNew &&
        !getIsEmptyData() &&
        !isResetSort
      ) {
        const fromStartToCurrentDailyCount = _.reduce(
          getDailyCount(),
          (result, value) =>
            value.date < selectedDateValue
              ? result + (value.count === -1 ? 0 : value.count)
              : result,
          0,
        );
        let haveEventOverTwoDays = false;
        let idxCount = 0;
        let foundStripIndex = -1;
        const combinedEvents = getCombinedEvents();
        _.forEach(combinedEvents, (x) => {
          if (x.oldType === activeButton) {
            if (selectedDateValue && x.start < selectedDateValue) {
              haveEventOverTwoDays = true;
            }
            if (x.idEvent === updatedSelectedStrip?.idEvent) {
              foundStripIndex = idxCount;
            }
            idxCount += 1;
          }
        });
        if (foundStripIndex > -1) {
          const newPageIndex = Math.floor(
            (foundStripIndex + fromStartToCurrentDailyCount + (haveEventOverTwoDays ? -1 : 0)) /
              STRIP_AFIB_THUMBNAIL_INFO.stripDisplayLimit,
          );
          setPageIndex((prev) => {
            if (newPageIndex !== prev.index) {
              return { index: newPageIndex };
            }
            return prev;
          });
        }
      }
    }
  }, [updatedSelectedStrip, eventFilter]);

  useImperativeHandle(ref, () => ({
    handleClickArrowButton: (isNext) => {
      if (chartHrRef.current) {
        chartHrRef.current.handleClickArrowButton(isNext);
      }
    },
  }));

  return {
    keyRecoil,
    dataChart,
    updatedSelectedStrip,
    chartHrRef,
  };
};

export default useAfibHrChart;
